import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MY_EXAMS, ADD_EXAM, DELETE_EXAM } from '../graphql';
import { Plus, Trash2, Clock, AlertCircle } from 'lucide-react';

export default function ExamsPage() {
  const { data, loading } = useQuery(MY_EXAMS);
  const [addExam] = useMutation(ADD_EXAM, { refetchQueries: [MY_EXAMS] });
  const [deleteExam] = useMutation(DELETE_EXAM, { refetchQueries: [MY_EXAMS] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', subject: '', examDate: '', notes: '' });

  const handleAdd = async (e) => {
    e.preventDefault();
    await addExam({ variables: { ...form, examDate: new Date(form.examDate).toISOString() } });
    setForm({ title: '', subject: '', examDate: '', notes: '' });
    setShowForm(false);
  };

  const urgencyColor = (days) => {
    if (days <= 3) return 'bg-red-100 text-red-700 border-red-200';
    if (days <= 7) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" /></div>;

  const exams = data?.myExams || [];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Exam Countdowns</h1>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(!showForm)}>
          <Plus size={18} /> Add Exam
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="card mb-6 space-y-4">
          <h3 className="font-semibold">New Exam</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input className="input" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="End Semester Exam" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input className="input" required value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
              <input className="input" type="datetime-local" required value={form.examDate} onChange={e => setForm({...form, examDate: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <input className="input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Chapters 1–5" />
            </div>
          </div>
          <div className="flex gap-3">
            <button className="btn-primary" type="submit">Save</button>
            <button className="btn-secondary" type="button" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {exams.map(exam => (
          <div key={exam.id} className={`card border ${urgencyColor(exam.daysLeft)}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {exam.daysLeft <= 3 && <AlertCircle size={16} />}
                  <h3 className="font-semibold">{exam.title}</h3>
                </div>
                <p className="text-sm opacity-70 mb-2">{exam.subject}</p>
                {exam.notes && <p className="text-xs opacity-60">{exam.notes}</p>}
              </div>
              <div className="flex items-center gap-3 ml-4">
                <div className="text-center">
                  <p className="text-3xl font-bold">{exam.daysLeft}</p>
                  <p className="text-xs opacity-60">days left</p>
                </div>
                <button onClick={() => deleteExam({ variables: { examId: exam.id }})}
                  className="p-1.5 opacity-50 hover:opacity-100 transition-opacity">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}

        {exams.length === 0 && !showForm && (
          <div className="text-center py-16 text-gray-400">
            <Clock size={48} className="mx-auto mb-3 opacity-30" />
            <p>No upcoming exams. Enjoy the peace! 😌</p>
          </div>
        )}
      </div>
    </div>
  );
}
